import { SpaceKey, SpaceKeySchema, UserId, UserIdSchema } from "../id"
import { DocumentSnapshotLike } from "../firestore"
import { z } from "zod/v4"
import { CreatedAt, UpdatedAt } from "./system"
import { UserDenormalizedWithLook, UserDocument, Users } from "./user"

export const SpaceUserRoleOwner = "owner"
export const SpaceUserRoleMember = "member"
export const SpaceUserRoleModerator = "moderator"

export const SpaceUserRole = z.union([
  z.literal(SpaceUserRoleOwner),
  z.literal(SpaceUserRoleModerator),
  z.literal(SpaceUserRoleMember),
])

export type SpaceUserRole = z.infer<typeof SpaceUserRole>

export const SpaceUserStatusValue = z.union([
  z.literal("online"),
  z.literal("offline"),
])

export type SpaceUserStatusValue = z.infer<typeof SpaceUserStatusValue>

export const SpaceUserStatus = z.union([
  z.object({
    status: z.literal("online"),
    muted: z.boolean(),
    last_joined_at: z.number().optional().describe("通話に参加した日時。"),
    last_connected_at: z.number().optional().describe("通話の参加中に行われるオンライン状態の死活監視の最終更新日時。"),
  }),
  z.object({
    status: z.literal("offline"),
    muted: z.null(),
    last_joined_at: z.number().optional().describe("通話に参加した日時。"),
    last_connected_at: z.number().optional().describe("通話の参加中に行われるオンライン状態の死活監視の最終更新日時。"),
  })
])

export type SpaceUserStatus = z.infer<typeof SpaceUserStatus>

export const SpaceUserDocumentMeta = z.intersection(
  SpaceUserStatus,
  z.object({
    user_id: UserIdSchema, // for collection group query
    space_key: SpaceKeySchema, // for collection group query
    user: UserDenormalizedWithLook.optional(),
    role: SpaceUserRole,
  })
)

export type SpaceUserDocumentMeta = z.infer<typeof SpaceUserDocumentMeta>

export const SpaceUserDocumentBody = z.intersection(
  SpaceUserDocumentMeta,
  z.object({
    created_at: CreatedAt,
    updated_at: UpdatedAt,
  })
)

export type SpaceUserDocumentBody = z.infer<typeof SpaceUserDocumentBody>

export const SpaceUserDocument = z.intersection(
  SpaceUserDocumentBody,
  z.object({
    id: UserIdSchema,
  })
)

export type SpaceUserDocument = z.infer<typeof SpaceUserDocument>

export class SpaceUserDocuments {

  static newUser(user: UserDocument, space_key: SpaceKey, role: SpaceUserRole): SpaceUserDocument {
    const now = new Date().getTime()
    return {
      id: user.id,
      user_id: user.id,
      space_key,
      role,
      status: "offline",
      muted: null,
      created_at: now,
      user: Users.toUserDenormalizedWithLook(user),
      updated_at: now,
    }
  }

  static toDocumentData(spaceUser: SpaceUserDocument): object {
    return {
      ...spaceUser,
      // id は document path に入るので入れない
      id: undefined,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): SpaceUserDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      id: UserId(doc.id),
      user_id: data["user_id"],
      user: data["user"] ? Users.parseDenormalizedWithLook(data["user"]) : undefined,
      space_key: data["space_key"],
      role: data["role"],
      status: data["status"],
      muted: data["muted"],
      last_connected_at: data["last_connected_at"],
      last_joined_at: data["last_joined_at"],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }
}

export const CreateSpaceUserRequest = z.object({
  inviteKey: z.string().optional(),
})
export type CreateSpaceUserRequest = z.infer<typeof CreateSpaceUserRequest>

export const CreateSpaceUserConnectionRequest = z.object({
  muted: z.boolean(),
})
export type CreateSpaceUserConnectionRequest = z.infer<typeof CreateSpaceUserConnectionRequest>

export const UpdateSpaceUserConnectionRequest = z.object({
  muted: z.boolean().optional(),
})
export type UpdateSpaceUserConnectionRequest = z.infer<typeof UpdateSpaceUserConnectionRequest>

export const UpdateSpaceUserMutedRequest = z.object({
  muted: z.boolean(),
})
export type UpdateSpaceUserMutedRequest = z.infer<typeof UpdateSpaceUserMutedRequest>

export const UpdateSpaceUserMutedByModeratorRequest = z.object({
  muted: z.literal(true),
})
export type UpdateSpaceUserMutedByModeratorRequest = z.infer<typeof UpdateSpaceUserMutedByModeratorRequest>

export const UpdateSpaceUserRoleRequest = z.object({
  role: SpaceUserRole
})
export type UpdateSpaceUserRoleRequest = z.infer<typeof UpdateSpaceUserRoleRequest>
